/**
 * TPRA Dashboard Vendors Table Column Definitions
 *
 * Defines columns for the All Third-Parties table with:
 * - Vendor logo rendering
 * - Created By column with avatars
 * - Issues by severity badges
 * - Status indicators
 */

import { VendorsAssessmentStatusLabelMap } from '@/modules/vendors/constants';
import ZAvatarGradient from '@/shared/components/avatarGradient';
import ZChip, { type ZChipColorValues } from '@/shared/components/Chip';
import type { ZTableColumnProps } from '@/shared/components/table';
import VendorLogo from '@/shared/components/VendorSearchAutocomplete/VendorLogo';
import { ZTooltip } from '@/shared/components/ZTooltip/ZTooltip';
import { Button, Chip, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@nextui-org/react';
import dayjs from 'dayjs';
import { Download, Ellipsis } from 'lucide-react';
import type { TPRAVendorDetails } from '../types/vendors';

export type OnSeverityClickHandler = (vendorName: string, severity: string) => void;

/**
 * Renders vendor logo with fallback handling
 */
function VendorLogoCell({ vendor }: { vendor: TPRAVendorDetails }) {
  const { vendor_logo_url, vendor_icon, vendor_product_name, vendor_name } = vendor;

  // First letter of vendor name for fallback avatar
  const firstLetter = vendor_name?.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center gap-2">
      {/* Render logo/icon if available */}
      {vendor_logo_url ? (
        <VendorLogo
          src={vendor_logo_url}
          alt={vendor_product_name || 'Vendor logo'}
          needsAuthToken={true}
          size={24}
          radius="sm"
          className="flex-shrink-0"
        />
      ) : (
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '24px',
            height: '24px',
            background: '#FAFAFA',
            border: '1px solid #E4E4E7',
            borderRadius: '24px',
            padding: '2px',
          }}
        >
          <span
            style={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              textAlign: 'center',
              color: '#71717A',
            }}
          >
            {firstLetter}
          </span>
        </div>
      )}

      {/* Vendor + Product name */}
      {vendor_product_name && <span className="text-[#333] break-all">{vendor_product_name}</span>}
    </div>
  );
}

/**
 * Renders Created By user with avatar
 */
function CreatedByCell({ vendor }: { vendor: TPRAVendorDetails }) {
  const { created_by_user } = vendor;

  if (!created_by_user || !created_by_user.name) {
    return null;
  }

  const { name } = created_by_user;

  return (
    <div className="flex items-center gap-3">
      <ZAvatarGradient name={name} size={24} textSize={10} />
      <p className="font-medium">{name}</p>
    </div>
  );
}

/**
 * Column definitions for TPRA Dashboard vendors table
 */
export const getTPRAVendorsListColumns = (
  onSeverityClick?: OnSeverityClickHandler
): ZTableColumnProps<TPRAVendorDetails>[] => [
  // Vendor & Product Name with Logo
  {
    type: 'custom',
    fieldName: 'vendor_product_name',
    headerName: 'Third-Party & Product',
    sortable: true,
    getSortValue: ({ vendor_product_name }) => vendor_product_name ?? '',
    renderCell: (vendor) => (
      <div className="min-h-[40px] flex items-center">
        <VendorLogoCell vendor={vendor} />
      </div>
    ),
  },

  // Risk
  {
    type: 'custom',
    fieldName: 'risk',
    headerName: 'Risk',
    sortable: true,
    renderCell: ({ risk }) => {
      if (!risk) {
        return null;
      }
      const chipColor: Record<string, ZChipColorValues> = {
        Low: 'green',
        Medium: 'yellow',
        High: 'red',
      };
      return <ZChip color={chipColor[risk]}>{risk}</ZChip>;
    },
  },

  // Last Assessed
  {
    type: 'custom',
    fieldName: 'last_assessed_at',
    headerName: 'Last Assessed',
    sortable: true,
    getSortValue: ({ last_assessed_at }) =>
      last_assessed_at && dayjs(last_assessed_at).isValid()
        ? dayjs(last_assessed_at).toDate().getTime()
        : 0,
    renderCell: ({ last_assessed_at }) => {
      if (!last_assessed_at || !dayjs(last_assessed_at).isValid()) {
        return <span className="text-gray-400">-</span>;
      }
      const parsedDate = dayjs.utc(last_assessed_at.replace('T', ' ').split('.')[0]);
      const localDate = parsedDate.local();
      return <div className="text-[#333]">{localDate.fromNow()}</div>;
    },
  },

  // Total Issues
  {
    type: 'custom',
    fieldName: 'total_issues',
    headerName: 'Total Issues',
    sortable: true,
    getSortValue: ({ total_issues }) => total_issues ?? 0,
    renderCell: ({ total_issues }) => (
      <div className="text-[#333] text-sm">
        {total_issues !== undefined ? total_issues : '-'}
      </div>
    ),
  },

  // Issues by Severity
  {
    type: 'custom',
    fieldName: 'issues_by_severity',
    headerName: 'Issues by Severity',
    renderCell: ({ issues_by_severity, vendor_name }) => {
      if (!issues_by_severity) {
        return <span className="text-gray-400">-</span>;
      }

      const { high, critical, medium, low } = issues_by_severity;
      const hasIssues = high + critical + medium + low > 0;

      if (!hasIssues) {
        return null;
      }

      const handleSeverityClick = (severity: string) => {
        if (onSeverityClick && vendor_name) {
          onSeverityClick(vendor_name, severity);
        }
      };

      return (
        <div className="flex items-center gap-1">
          {/* Critical Issues */}
          {critical > 0 && (
            <ZTooltip content="Critical Issues - Click to view" placement="top" showArrow>
              <div
                className="flex flex-row justify-center items-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  padding: '4px 8px',
                  gap: '4px',
                  minWidth: '24px',
                  height: '20px',
                  background: 'linear-gradient(0deg, #FECACA, #FECACA), #FFFFFF',
                  borderRadius: '58px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSeverityClick('Critical');
                }}
              >
                <span
                  className="font-inter font-medium text-center"
                  style={{
                    height: '12px',
                    fontSize: '10px',
                    lineHeight: '12px',
                    color: '#991B1B',
                  }}
                >
                  {critical}
                </span>
              </div>
            </ZTooltip>
          )}

          {/* High Severity */}
          {high > 0 && (
            <ZTooltip content="High Severity Issues - Click to view" placement="top" showArrow>
              <div
                className="flex flex-row justify-center items-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  padding: '4px 8px',
                  gap: '4px',
                  minWidth: '24px',
                  height: '20px',
                  background: 'linear-gradient(0deg, #FDD0DF, #FDD0DF), #FFFFFF',
                  borderRadius: '58px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSeverityClick('High');
                }}
              >
                <span
                  className="font-inter font-medium text-center"
                  style={{
                    height: '12px',
                    fontSize: '10px',
                    lineHeight: '12px',
                    color: '#F31260',
                  }}
                >
                  {high}
                </span>
              </div>
            </ZTooltip>
          )}

          {/* Medium Severity */}
          {medium > 0 && (
            <ZTooltip content="Medium Severity Issues - Click to view" placement="top" showArrow>
              <div
                className="flex flex-row justify-center items-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  padding: '4px 8px',
                  gap: '4px',
                  minWidth: '24px',
                  height: '20px',
                  background: 'linear-gradient(0deg, #FEF3C7, #FEF3C7), #FFFFFF',
                  borderRadius: '58px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSeverityClick('Medium');
                }}
              >
                <span
                  className="font-inter font-medium text-center"
                  style={{
                    height: '12px',
                    fontSize: '10px',
                    lineHeight: '12px',
                    color: '#D97706',
                  }}
                >
                  {medium}
                </span>
              </div>
            </ZTooltip>
          )}

          {/* Low Severity */}
          {low > 0 && (
            <ZTooltip content="Low Severity Issues - Click to view" placement="top" showArrow>
              <div
                className="flex flex-row justify-center items-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  padding: '4px 8px',
                  gap: '4px',
                  minWidth: '24px',
                  height: '20px',
                  background: 'linear-gradient(0deg, #D1FAE5, #D1FAE5), #FFFFFF',
                  borderRadius: '58px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSeverityClick('Low');
                }}
              >
                <span
                  className="font-inter font-medium text-center"
                  style={{
                    height: '12px',
                    fontSize: '10px',
                    lineHeight: '12px',
                    color: '#065F46',
                  }}
                >
                  {low}
                </span>
              </div>
            </ZTooltip>
          )}
        </div>
      );
    },
  },

  // Status
  {
    type: 'custom',
    fieldName: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: ({ status }) => {
      if (!status) return '';

      const DisplayVendorDetails = VendorsAssessmentStatusLabelMap[status];

      if (!DisplayVendorDetails) return '';

      return (
        <div className="flex items-center gap-3">
          <Chip
            variant="bordered"
            radius="full"
            className="h-6 border-1.5"
            classNames={{
              content: 'px-1 py-2 flex items-center gap-1',
            }}
            style={{
              color: DisplayVendorDetails.borderColor ?? 'black',
              borderColor: DisplayVendorDetails.borderColor ?? 'black',
              backgroundColor: DisplayVendorDetails.backgroundColor ?? 'transparent',
            }}
          >
            {DisplayVendorDetails.renderIcon?.()}
          </Chip>
          <div className="text-[#333]">{DisplayVendorDetails.label}</div>
        </div>
      );
    },
  },

  // Created By (NEW COLUMN)
  {
    type: 'custom',
    fieldName: 'created_by_user',
    headerName: 'Created By',
    renderCell: (vendor) => (
      <div className="min-h-[40px] flex items-center">
        <CreatedByCell vendor={vendor} />
      </div>
    ),
  },

  // Actions
  {
    type: 'custom',
    fieldName: 'actions',
    headerName: '',
    headerClassName: '!w-[60px]',
    renderCell: () => {
      return (
        <div className="flex justify-end w-full items-center h-full min-h-[40px]">
          <Dropdown>
            <DropdownTrigger>
              <Button isIconOnly className="bg-transparent hover:bg-gray-100 transition-colors">
                <Ellipsis className="h-5 w-5" color="#717179" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu>
              <DropdownItem
                key="download-summary"
                startContent={<Download className="h-4 w-4 text-[#444444]" />}
                className="text-[#444444]"
                isDisabled
              >
                Download Summary
              </DropdownItem>
              <DropdownItem
                key="download-report"
                startContent={<Download className="h-4 w-4 text-[#444444]" />}
                className="text-[#444444]"
                isDisabled
              >
                Download Report
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    },
  },
];
